import { useState, useEffect, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Typography,
  Link,
  CircularProgress,
  ListItemText,
  Tooltip,
  IconButton
} from '@mui/material';
import { FileText, Banknote, CreditCard, Smartphone } from 'lucide-react';
import { openAdminReceiptPrint, resolveReceiptRowForPrint } from '../utils/openAdminReceiptPrint.js';
import { adminAPI, eventBatchAPI, trekAPI } from '../services/api';
import {
  registrationBatchListParams,
  mergeBatchIntoRegistrationSelect,
  registrationBatchOptionLabel
} from '../utils/registrationPickerBatches.js';
import { useToast } from '../context/ToastContext';
import { useAuth } from '../context/AuthContext';
import { capitalizeNamePart } from '../utils/capitalizePersonName.js';
import { REGISTRATION_STATUS_OPTIONS } from '../constants/registrationStatuses.js';
import { packageDetailSummary } from '../utils/packageDisplayText.js';
import { formatAdminInr } from '../utils/formatAdminPrice.js';
import AdminDatePicker from './AdminDatePicker.jsx';
import { formatCommentTimestamp } from '../utils/adminCommentDisplay.js';
import {
  buildRegistrationCommentHoverLines,
  buildRegistrationPaymentHoverLines
} from '../utils/registrationStatusHover.js';

function formatDateKey(d) {
  if (!d) return '—';
  const x = new Date(d);
  if (isNaN(x.getTime())) return '—';
  const y = x.getFullYear();
  const m = String(x.getMonth() + 1).padStart(2, '0');
  const day = String(x.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function formatRegisteredOn(d) {
  if (!d) return '—';
  const x = new Date(d);
  return isNaN(x.getTime()) ? '—' : x.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
}

const GENDER_OPTIONS = [
  { value: 'male', label: 'Male' },
  { value: 'female', label: 'Female' },
  { value: 'other', label: 'Other' }
];

function stripGroupNumberSuffix(name) {
  return String(name || '')
    .replace(/\s*\(#\d+\)\s*$/i, '')
    .trim();
}

const emptyForm = () => ({
  trekId: '',
  batchId: '',
  joinFromPackageId: '',
  registrationStatus: 'pending',
  groupAssignment: '',
  groupName: '',
  firstName: '',
  lastName: '',
  phone: '',
  gender: '',
  birthdate: '',
  stayOption: '',
  newComment: ''
});

function defaultStayOptionForTrek(trek) {
  const tr = trek || {};
  if (Array.isArray(tr.stayOptions) && tr.stayOptions.length > 0) {
    return String(tr.stayOptions[0].id ?? '');
  }
  return 'quad';
}

export default function EditRegistrationModal({ open, bookingId, onClose, onSaved }) {
  const queryClient = useQueryClient();
  const { showToast } = useToast();
  const { permissions } = useAuth();
  const [form, setForm] = useState(emptyForm);

  const canSave =
    permissions.includes('*') ||
    permissions.includes('tickets_manage') ||
    permissions.includes('registrations_manage');

  const { data: bookingRes, isLoading: loadingBooking } = useQuery({
    queryKey: ['admin-booking-detail', bookingId],
    queryFn: () => adminAPI.getBooking(bookingId),
    enabled: open && !!bookingId
  });

  const detail = bookingRes?.data?.data;

  const { data: treksData } = useQuery({
    queryKey: ['admin-treks-edit-registration-modal'],
    queryFn: () => trekAPI.list({ page: 1, limit: 500, isActive: true }),
    enabled: open
  });
  const treks = treksData?.data?.data || [];
  const trekFromBooking = detail?.batch?.trek;
  const trekOptions = useMemo(() => {
    const map = new Map();
    (treks || []).forEach((t) => map.set(t.id, t));
    if (trekFromBooking?.id) map.set(trekFromBooking.id, trekFromBooking);
    return [...map.values()];
  }, [treks, trekFromBooking]);

  const trekIdForBatches = form.trekId || '';
  const { data: batchesData } = useQuery({
    queryKey: ['admin-event-batches-edit-modal', trekIdForBatches],
    queryFn: () =>
      eventBatchAPI.list({
        ...registrationBatchListParams(),
        trekId: trekIdForBatches || undefined
      }),
    enabled: open && !!trekIdForBatches
  });
  const allBatches = batchesData?.data?.data || batchesData?.data || [];
  const batches = useMemo(() => {
    const filtered = trekIdForBatches
      ? allBatches.filter((b) => String(b.trekId || b.trek?.id) === String(trekIdForBatches))
      : [];
    return mergeBatchIntoRegistrationSelect(filtered, detail?.batch);
  }, [allBatches, trekIdForBatches, detail?.batch]);

  const { data: eventGroupsRes } = useQuery({
    queryKey: ['admin-event-groups-edit-modal', form.batchId],
    queryFn: () => adminAPI.listEventRegistrationGroups(form.batchId),
    enabled: open && !!form.batchId,
    staleTime: 30_000
  });
  const eventGroups = eventGroupsRes?.data?.data || [];

  const trekForStay = useMemo(() => {
    const ev = detail?.batch;
    const detailTrek =
      ev?.trek && String(ev.id || detail?.batchId) === String(form.batchId) ? ev.trek : null;
    const fromBatch = batches.find((b) => String(b.id) === String(form.batchId));
    // Booking GET loads full trek (stay_options). Event batch list uses a slim trek — prefer detail first.
    if (detailTrek) return detailTrek;
    if (fromBatch?.trek) return fromBatch.trek;
    return trekFromBooking || null;
  }, [batches, form.batchId, detail?.batch, detail?.batchId, trekFromBooking]);

  const stayChoices = useMemo(() => {
    const t = trekForStay;
    if (!t) return [];
    if (Array.isArray(t.stayOptions) && t.stayOptions.length > 0) {
      return t.stayOptions.map((o) => ({
        value: String(o.id ?? ''),
        label: (String(o.name || '').trim() || String(o.id ?? '')).trim() || `Stay ${o.id}`,
        priceAdjust: Number(o.priceAdjust) || 0
      }));
    }
    return [
      { value: 'quad', label: 'Group sharing', priceAdjust: 0 },
      { value: 'triple', label: 'Triple sharing', priceAdjust: 0 },
      { value: 'double', label: 'Double sharing', priceAdjust: 0 }
    ];
  }, [trekForStay]);

  useEffect(() => {
    if (!detail || !open) return;
    const batch = detail.batch;
    const trek = batch?.trek;
    const reg = detail.registration || {};
    const primary = Array.isArray(detail.travelers) && detail.travelers[0] ? detail.travelers[0] : {};
    const dob =
      primary.birthdate ||
      primary.dob ||
      reg.dob ||
      reg.dateOfBirth ||
      null;
    const dobStr = dob ? String(dob).slice(0, 10) : '';
    const pkgId =
      detail.joinFromPackageId ??
      reg.packageId ??
      null;
    const regStatus =
      reg.status != null && String(reg.status).trim()
        ? String(reg.status).trim().toLowerCase()
        : 'pending';
    const group = reg.group || null;
    const groupId = group?.id ?? reg.groupId ?? null;
    const groupName = stripGroupNumberSuffix(group?.groupName || detail.groupName);
    setForm({
      trekId: String(trek?.id || ''),
      batchId: String(detail.batchId || batch?.id || ''),
      joinFromPackageId: pkgId != null && pkgId !== '' ? String(pkgId) : '',
      registrationStatus: regStatus,
      groupAssignment: groupId ? String(groupId) : '',
      groupName,
      firstName: capitalizeNamePart(primary.firstName || reg.firstName || ''),
      lastName: capitalizeNamePart(primary.lastName || reg.lastName || ''),
      phone: primary.phone ? String(primary.phone) : reg.phone ? String(reg.phone) : '',
      gender: (primary.gender ? String(primary.gender) : reg.gender ? String(reg.gender) : '')
        .toLowerCase(),
      birthdate: dobStr,
      stayOption:
        detail.stayOption != null && String(detail.stayOption).trim() !== ''
          ? String(detail.stayOption).trim()
          : '',
      newComment: ''
    });
  }, [detail, open]);

  /** Booking GET includes full packages (with names). Batch list may use a slim package shape — prefer detail so the Select shows labels. */
  const packages = useMemo(() => {
    const detailTrekId = detail?.batch?.trek?.id;
    const formTrekId = form.trekId ? String(form.trekId) : '';
    const fromDetail =
      Array.isArray(detail?.batch?.trek?.packages) &&
      (!formTrekId || !detailTrekId || String(detailTrekId) === formTrekId)
        ? detail.batch.trek.packages
        : [];
    if (fromDetail.length) return fromDetail;
    const batch = batches.find((b) => String(b.id) === String(form.batchId));
    const forBatch = batch?.trek?.packages;
    return Array.isArray(forBatch) ? forBatch : [];
  }, [detail, batches, form.batchId, form.trekId]);

  /** Same 12 values as the Registrations page Status filter (no “All”); include legacy DB values. */
  const statusOptions = useMemo(() => {
    const raw = form.registrationStatus ? String(form.registrationStatus).trim().toLowerCase() : '';
    const base = [...REGISTRATION_STATUS_OPTIONS];
    if (raw && !base.some((o) => o.value === raw)) {
      base.unshift({
        value: raw,
        label: `${raw.replace(/_/g, ' ')} (current)`
      });
    }
    return base;
  }, [form.registrationStatus]);

  const saveMutation = useMutation(
    (body) => adminAPI.patchAdminBooking(bookingId, body),
    {
      onSuccess: (_data, variables) => {
        queryClient.invalidateQueries(['admin-booking-detail', bookingId]);
        queryClient.invalidateQueries('admin-ticket-bookings');
        queryClient.invalidateQueries('admin-bookings');
        const addedComment = Boolean(variables?.newComment);
        showToast({
          message: addedComment ? 'Comment added.' : 'Registration saved.',
          severity: 'success'
        });
        onSaved?.();
        if (addedComment) {
          setForm((f) => ({ ...f, newComment: '' }));
        } else {
          onClose();
        }
      },
      onError: (err) =>
        showToast({
          message: err.response?.data?.message || 'Failed to save registration.',
          severity: 'error'
        })
    }
  );

  const handleTrekChange = (trekId) => {
    const tid = String(trekId);
    setForm((f) => ({
      ...f,
      trekId: tid,
      batchId: '',
      joinFromPackageId: '',
      stayOption: '',
      groupAssignment: '',
      groupName: ''
    }));
    eventBatchAPI.list({ limit: 500, trekId: tid, sortBy: 'startDate', sortOrder: 'asc' }).then((res) => {
      const list = sortEventBatchesByStartDateAsc(res?.data?.data || res?.data || []);
      const first = list[0];
      const pkgs = first?.trek?.packages || [];
      const main = pkgs.find((p) => p.isMain) || pkgs[0];
      const stayOpt = defaultStayOptionForTrek(first?.trek);
      setForm((f) => {
        if (f.trekId !== tid) return f;
        return {
          ...f,
          batchId: first ? String(first.id) : '',
          joinFromPackageId: main ? String(main.id) : '',
          stayOption: stayOpt
        };
      });
    });
  };

  const handleSave = () => {
    if (!bookingId) return;
    if (!form.firstName?.trim() || !form.lastName?.trim()) {
      showToast({ message: 'First and last name are required.', severity: 'warning' });
      return;
    }
    if (form.groupAssignment === '__new__' && !form.groupName?.trim()) {
      showToast({ message: 'Enter a group name for the new group.', severity: 'warning' });
      return;
    }
    const body = {
      batchId: form.batchId ? Number(form.batchId) : undefined,
      joinFromPackageId: form.joinFromPackageId ? Number(form.joinFromPackageId) : null,
      registrationStatus: form.registrationStatus,
      firstName: capitalizeNamePart(form.firstName),
      lastName: capitalizeNamePart(form.lastName),
      phone: form.phone,
      gender: form.gender || null,
      birthdate: form.birthdate?.trim() || null,
      stayOption: form.stayOption?.trim() ? form.stayOption.trim() : null,
      newComment: form.newComment?.trim() || undefined
    };
    if (form.groupAssignment === '') {
      body.groupId = null;
      body.groupName = '';
    } else if (form.groupAssignment === '__new__') {
      body.groupName = form.groupName.trim();
    } else {
      body.groupId = Number(form.groupAssignment);
      if (form.groupName?.trim()) body.groupName = form.groupName.trim();
    }
    saveMutation.mutate(body);
  };

  const paymentLines = detail ? buildRegistrationPaymentHoverLines(detail) : [];
  const commentLines = detail ? buildRegistrationCommentHoverLines(detail) : [];
  const hasRecordedPayment = paymentLines.length > 0;

  const regUser = detail?.registration?.user;
  const registeredByPhone = regUser?.phone ? String(regUser.phone) : null;

  const PaymentIcon = ({ method }) => {
    if (!method) return <Banknote size={14} />;
    const m = String(method).toLowerCase();
    if (m.includes('online') || m.includes('razorpay') || m.includes('card')) return <CreditCard size={14} />;
    if (m.includes('upi')) return <Smartphone size={14} />;
    return <Banknote size={14} />;
  };


  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth scroll="paper" PaperProps={{ sx: { maxWidth: 720 } }}>
      <DialogTitle sx={{ pr: 5 }}>Edit registration</DialogTitle>
      <DialogContent dividers>
        {loadingBooking || !detail ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={32} />
          </Box>
        ) : (
          <Box sx={{ 
            display: 'grid', 
            gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, 
            gap: 2, 
            pt: 0.5, 
            alignItems: 'start',
            '& .MuiInputBase-input': { fontSize: '0.85rem' },
            '& .MuiInputLabel-root': { fontSize: '0.85rem' },
            '& .MuiMenuItem-root': { fontSize: '0.85rem' },
            '& .MuiSelect-select': { fontSize: '0.85rem' }
          }}>
            {/* Row 1: Reg ID and Trek */}
            <Box sx={{ display: 'flex', alignItems: 'center', minHeight: '40px' }}>
              <Typography variant="body2" color="text.secondary" component="div">
                Registration{' '}
                {detail.registrationId != null ? (
                  <strong>{detail.registrationId}</strong>
                ) : (
                  <strong>—</strong>
                )}
              </Typography>
            </Box>

            <FormControl fullWidth size="small" disabled={!canSave}>
              <InputLabel>Trek</InputLabel>
              <Select
                label="Trek"
                value={form.trekId}
                onChange={(e) => handleTrekChange(e.target.value)}
              >
                {trekOptions.map((t) => (
                  <MenuItem key={t.id} value={String(t.id)}>
                    {t.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Row 2: First Name and Last Name */}
            <TextField
              size="small"
              fullWidth
              label="First name"
              value={form.firstName}
              onChange={(e) => setForm((f) => ({ ...f, firstName: e.target.value }))}
              disabled={!canSave}
            />
            <TextField
              size="small"
              fullWidth
              label="Last name"
              value={form.lastName}
              onChange={(e) => setForm((f) => ({ ...f, lastName: e.target.value }))}
              disabled={!canSave}
            />

            {/* Row 3: Event and Package */}
            <FormControl fullWidth size="small" disabled={!canSave || !form.trekId}>
              <InputLabel>Event</InputLabel>
              <Select
                label="Event"
                value={form.batchId}
                onChange={(e) => {
                  const bid = e.target.value;
                  const b = batches.find((x) => String(x.id) === String(bid));
                  const stayOpt = defaultStayOptionForTrek(b?.trek);
                  setForm((f) => ({
                    ...f,
                    batchId: bid,
                    stayOption: stayOpt,
                    groupAssignment: '',
                    groupName: ''
                  }));
                }}
              >
                {batches.map((b) => (
                  <MenuItem key={b.id} value={String(b.id)}>
                    {registrationBatchOptionLabel(b)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth size="small" disabled={!canSave || !packages.length}>
              <InputLabel>Package</InputLabel>
              <Select
                label="Package"
                value={form.joinFromPackageId}
                onChange={(e) => setForm((f) => ({ ...f, joinFromPackageId: e.target.value }))}
                renderValue={(selectedId) => {
                  const p = packages.find((x) => String(x.id) === String(selectedId));
                  if (!p) return '';
                  const title = p.name?.trim() ? p.name : `Package #${p.id}`;
                  const detail = packageDetailSummary(p);
                  const displayStr = detail ? `${title} - ${detail}` : title;
                  return (
                    <Box sx={{ overflow: 'hidden' }}>
                      <Typography component="span" variant="body2" noWrap display="block">
                        {displayStr}
                      </Typography>
                    </Box>
                  );
                }}
              >
                {packages.map((p) => {
                  const title = p.name?.trim() ? p.name : `Package #${p.id}`;
                  const detail = packageDetailSummary(p);
                  const displayStr = detail ? `${title} - ${detail}` : title;
                  return (
                    <MenuItem key={p.id} value={String(p.id)} sx={{ alignItems: 'flex-start', py: 1 }}>
                      <ListItemText
                        primary={displayStr}
                        primaryTypographyProps={{ variant: 'body2' }}
                      />
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>

            {/* Row 4: Stay and Group */}
            <FormControl fullWidth size="small" disabled={!canSave || stayChoices.length === 0}>
              <InputLabel>Stay option</InputLabel>
              <Select
                label="Stay option"
                value={form.stayOption || ''}
                onChange={(e) => setForm((f) => ({ ...f, stayOption: e.target.value }))}
              >
                <MenuItem value="">
                  <em>None</em>
                </MenuItem>
                {form.stayOption &&
                !stayChoices.some((o) => o.value === form.stayOption) ? (
                  <MenuItem value={form.stayOption}>Current value ({form.stayOption})</MenuItem>
                ) : null}
                {stayChoices.map((o) => (
                  <MenuItem key={o.value} value={o.value}>
                    <Box sx={{ display: 'flex', alignItems: 'baseline', flexWrap: 'wrap', gap: 0.5 }}>
                      <span>{o.label}</span>
                      {Number(o.priceAdjust) !== 0 ? (
                        <Typography component="span" variant="caption" color="text.secondary">
                          ({Number(o.priceAdjust) > 0 ? '+' : ''}
                          {formatAdminInr(o.priceAdjust)})
                        </Typography>
                      ) : null}
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <FormControl fullWidth size="small" disabled={!canSave || !form.batchId}>
                <InputLabel>Group</InputLabel>
                <Select
                  label="Group"
                  value={form.groupAssignment}
                  onChange={(e) => {
                    const next = e.target.value;
                    setForm((f) => {
                      if (next === '__new__') {
                        return { ...f, groupAssignment: next, groupName: '' };
                      }
                      if (next === '') {
                        return { ...f, groupAssignment: '', groupName: '' };
                      }
                      const hit = eventGroups.find((g) => String(g.id) === String(next));
                      return {
                        ...f,
                        groupAssignment: next,
                        groupName: hit?.groupName
                          ? stripGroupNumberSuffix(hit.groupName)
                          : f.groupName
                      };
                    });
                  }}
                >
                  <MenuItem value="">
                    <em>No group</em>
                  </MenuItem>
                  {form.groupAssignment &&
                  form.groupAssignment !== '__new__' &&
                  !eventGroups.some((g) => String(g.id) === String(form.groupAssignment)) ? (
                    <MenuItem value={form.groupAssignment}>
                      Current group (#{form.groupAssignment})
                    </MenuItem>
                  ) : null}
                  {eventGroups.map((g) => (
                    <MenuItem key={g.id} value={String(g.id)}>
                      {stripGroupNumberSuffix(g.groupName) || `Group #${g.id}`}
                      {` (#${g.id})`}
                      {g.members != null ? ` · ${g.members} member${Number(g.members) === 1 ? '' : 's'}` : ''}
                    </MenuItem>
                  ))}
                  <MenuItem value="__new__">Create new group…</MenuItem>
                </Select>
              </FormControl>
              {form.groupAssignment ? (
                <TextField
                  size="small"
                  fullWidth
                  label="Group name"
                  value={form.groupName}
                  onChange={(e) => setForm((f) => ({ ...f, groupName: e.target.value }))}
                  disabled={!canSave}
                  helperText={
                    form.groupAssignment === '__new__'
                      ? 'Creates a new group.'
                      : 'Renaming updates the label for all members.'
                  }
                />
              ) : null}
            </Box>

            {/* Row 5: Gender and DOB */}
            <FormControl fullWidth size="small" disabled={!canSave}>
              <InputLabel>Gender</InputLabel>
              <Select
                label="Gender"
                value={form.gender}
                onChange={(e) => setForm((f) => ({ ...f, gender: e.target.value }))}
              >
                <MenuItem value="">
                  <em>—</em>
                </MenuItem>
                {GENDER_OPTIONS.map((o) => (
                  <MenuItem key={o.value} value={o.value}>
                    {o.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <AdminDatePicker
              label="Birth date"
              value={form.birthdate}
              onChange={(birthdate) => setForm((f) => ({ ...f, birthdate }))}
              disabled={!canSave}
              sx={{ width: '100%' }}
            />

            {/* Row 6: Phone Number and Status */}
            <TextField
              size="small"
              fullWidth
              label="Phone"
              value={form.phone}
              onChange={(e) =>
                setForm((f) => ({
                  ...f,
                  phone: String(e.target.value || '')
                    .replace(/\D/g, '')
                    .slice(0, 10)
                }))
              }
              inputProps={{ type: 'tel', inputMode: 'numeric', maxLength: 10, pattern: '[6-9][0-9]{9}' }}
              helperText="10-digit mobile starting with 6–9"
              disabled={!canSave}
              placeholder="10-digit mobile"
            />

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <FormControl fullWidth size="small" disabled={!canSave}>
                <InputLabel>Status</InputLabel>
                <Select
                  label="Status"
                  value={form.registrationStatus}
                  onChange={(e) => setForm((f) => ({ ...f, registrationStatus: e.target.value }))}
                >
                  {statusOptions.map((o) => (
                    <MenuItem key={o.value} value={o.value}>
                      {o.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              {form.registrationStatus === 'instructor' ||
              form.registrationStatus === 'volunteer' ||
              form.registrationStatus === 'camp_leader' ? (
                <Typography variant="caption" color="text.secondary">
                  Staff registration ({form.registrationStatus.replace(/_/g, ' ')}) — payment is usually not required.
                </Typography>
              ) : null}
            </Box>

            <Box sx={{ gridColumn: { sm: 'span 2' } }}>
              <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                Payment details
              </Typography>
              {paymentLines.length ? (
                <Box
                  component="ul"
                  sx={{
                    m: 0,
                    pl: 2.25,
                    mb: 1.5,
                    fontSize: '0.875rem',
                    color: 'text.secondary',
                    '& li': { mb: 0.35 }
                  }}
                >
                  {paymentLines.map((line) => {
                    const lineDate = line.printReceipt?.paidAt || line.printReceipt?.createdAt;
                    const dateStr = lineDate ? new Date(lineDate).toLocaleDateString('en-GB') : '';
                    return (
                      <Box component="li" key={line.id} sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                        <PaymentIcon method={line.method} />
                        <Typography variant="body2" color="text.primary">
                          {line.method} - {line.amountLabel} {dateStr ? `- ${dateStr}` : ''}
                        </Typography>
                        {line.printReceipt && (
                          <IconButton
                            size="small"
                            title="View Receipt"
                            onClick={() => {
                              try {
                                const row = resolveReceiptRowForPrint(line, detail);
                                if (row) openAdminReceiptPrint(row, detail);
                              } catch (e) {
                                if (e?.message === 'POPUP_BLOCKED') alert('Please allow popups for this site to view receipts.');
                              }
                            }}
                          >
                            <FileText size={16} />
                          </IconButton>
                        )}
                      </Box>
                    );
                  })}
                </Box>
              ) : (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                  No payments recorded yet.
                </Typography>
              )}

              <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                Comments
              </Typography>
              <TextField
                size="small"
                fullWidth
                multiline
                minRows={2}
                label="Add comment"
                placeholder="Visible to admins only"
                value={form.newComment}
                onChange={(e) => setForm((f) => ({ ...f, newComment: e.target.value }))}
                disabled={!canSave}
              />
              {commentLines.length > 0 ? (
                <Box
                  sx={{
                    mt: 1.5,
                    maxHeight: 220,
                    overflow: 'auto',
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                    p: 1.25,
                    bgcolor: 'action.hover'
                  }}
                >
                  {commentLines.map((line, i) => {
                    const when = line.when ? formatCommentTimestamp(line.when) : null;
                    return (
                      <Box
                        key={`comment-line-${i}`}
                        sx={{
                          mb: i < commentLines.length - 1 ? 1.25 : 0,
                          pb: i < commentLines.length - 1 ? 1.25 : 0,
                          borderBottom: i < commentLines.length - 1 ? '1px solid' : 'none',
                          borderColor: 'divider'
                        }}
                      >
                        {line.author || when ? (
                          <Typography variant="body2" component="div" sx={{ lineHeight: 1.5 }}>
                            {line.author ? (
                              <Typography component="span" variant="body2" fontWeight={600}>
                                {line.author}
                              </Typography>
                            ) : null}
                            {when ? (
                              <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: line.author ? 0.75 : 0 }}>
                                {when}
                              </Typography>
                            ) : null}
                          </Typography>
                        ) : null}
                        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', mt: 0.25 }}>
                          {line.text}
                        </Typography>
                      </Box>
                    );
                  })}
                </Box>
              ) : null}
            </Box>

            {!canSave ? (
              <Typography variant="body2" color="warning.main">
                You can view this registration. Saving requires Tickets manage or Registrations manage permission.
              </Typography>
            ) : null}
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={!canSave || saveMutation.isLoading || loadingBooking || !detail}
        >
          {saveMutation.isLoading ? 'Saving…' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
